import Tooltip from '@/componentsDS/Tooltip/Tooltip';
import Icon from '@/componentsDS/Icon/Icon';
import classNames from 'classnames';
import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import useChipInput from '@/hooksDS/useChipInput';
import { handleClearChip, handleClearAllChips } from '@/utils/chipHelpers';
import ChipField from '@/componentsDS/ChipField';
import InputField from '@/componentsDS/InputField';
import Dropdown from '@/componentsDS/Dropdown/Dropdown';
import { InputPropTypes } from './Input.types';

const Input = forwardRef((
	{
		id,
		name,
		type = 'text',
		label,
		placeholder,
		disabled = false,
		required = false,
		errorMessage,
		infoText,
		captionText,
		value = '',
		onChange,
		onClick,
		variant = 'outlined',
		fullWidth = false,
		size = 'default',
		labelInfoText,
		addClassName,
		prefixIcon,
		suffixIcon,
		prefixDropdown,
		prefixDropdownOption,
		prefixDropdownValue,
		prefixDropdownSelectedOption,
		prefixDropdownOnAdd,
		suffixDropdown,
		suffixDropdownOption,
		suffixDropdownValue,
		suffixDropdownSelectedOption,
		suffixDropdownOnAdd,
		chipInputMultiple,
		chipValues,
		setChipValues,
		allowChipModify,
		onClearChips,
		onBlur,
		disableClear = false,
		register,
		pipeText,
		keyPress,
		setValue,
		onCancel,
		labelInfoIcon,

		...inputProps
	},
	ref
) => {
	const {
		inputValue,
		hasChips,
		handleOnChange,
		handleOnClear,
		handleKeyPress,
	} = useChipInput({
		initialValue: value,
		onChange,
		setValue,
		name,
		chipValues,
		allowChipModify,
		chipInputMultiple
	});

	const handleChipRemoval = (id) => handleClearChip(id, chipValues, setChipValues);
	const handleClearAllChip = () => handleClearAllChips(onClearChips, setChipValues);

	const inputClass = classNames('DS__input', addClassName, {
		'DS__input--fullWidth': fullWidth,
		'DS__input--small': size === "small" && !hasChips,
		'DS__input--disabled': disabled,
		'DS__input--error': errorMessage,
	});

	const inputContainerClass = classNames({
		'DS__input-container': !hasChips,
		'DS__chip-input-container': hasChips && !chipInputMultiple,
		'DS__chip-input-container--multiple': chipInputMultiple,
		'DS__input-container--outlined': variant === "outlined",
		'DS__input-container--filled': variant === "filled",
		'DS__input-container--with-value': inputValue,
		'DS__input-container--error': errorMessage,
		'DS__input-container--info': infoText,
		'DS__input-container--disabled': disabled
	});

	const inputFieldClass = classNames({
		'DS__input-field': !hasChips,
		'DS__chip-input-field': hasChips,
		'DS__input-field--with-prefix': prefixDropdown,
		'DS__input-field--with-suffix': (suffixIcon && inputValue) || suffixDropdown
	});

	const inputLabelClass = classNames('DS__input-label', {
		'DS__input-label--disabled': (!hasChips && !inputValue && disabled) || (hasChips && disabled && chipValues?.length === 0)
	});

	const placeHolderText = (!chipInputMultiple && (chipValues?.length ?? 0) > 0) || (chipInputMultiple && disabled && (chipValues?.length ?? 0) > 0) ? '' : placeholder || 'Enter Text';

	return (
		<div className={inputClass} data-testid="input-field">
			{label && (
				<label htmlFor={id} className={inputLabelClass} data-testid="input-field-label">
					{required && <span className="DS__input-label__asterisk">*</span>}
					{label}
					{labelInfoText && (
						<Tooltip id={labelInfoText} content={labelInfoText}>
							<Icon name="information-circle" style={{ type: "solid" }} size="small" />
						</Tooltip>
					)}
				</label>
			)}
			<div className={inputContainerClass}>
				{prefixIcon && <div className="DS__input__prefix-icon">{prefixIcon}</div>}
				{prefixDropdown && (
					<Dropdown
						options={prefixDropdownOption}
						value={prefixDropdownSelectedOption}
						onAdd={prefixDropdownOnAdd}
						trigger={
							<div className="DS__input__prefix-dropdown" data-testid="input-prefix-dropdown">
								<span>{prefixDropdownValue}</span>
								<Icon name="chevron-down" style={{ type: "solid" }} size="small" />
							</div>
						}
						multiple={false}
						disabled={disabled}
						offset={size === "small" ? [0, -2] : [0, 4]}
						size={size}
					/>
				)}

				{hasChips && (
					<ChipField
						chipValues={chipValues}
						chipInputMultiple={chipInputMultiple}
						disabled={disabled}
						onRemoveChip={handleChipRemoval}
						onRemoveAll={handleClearAllChip}
					/>
				)}

				<InputField
					id={id}
					name={name}
					inputValue={inputValue}
					type={type}
					placeholder={placeHolderText}
					disabled={disabled}
					onChange={handleOnChange}
					onKeyDown={handleKeyPress}
					onClick={onClick}
					register={register}
					ref={ref}
					hasChips={hasChips}
					chipInputMultiple={chipInputMultiple}
					disableClear={disableClear}
					onClear={handleOnClear}
					className={inputFieldClass}
					inputProps={inputProps}
				/>

				{suffixIcon && <div className="DS__input__suffix-icon">{suffixIcon}</div>}
				{suffixDropdown && (


					<Dropdown
						options={suffixDropdownOption}
						value={suffixDropdownSelectedOption}
						onAdd={suffixDropdownOnAdd}
						trigger={
							<div className="DS__input__suffix-dropdown" data-testid="input-suffix-dropdown">
								{suffixDropdownValue}
								<Icon name="chevron-down" style={{ type: "solid" }} size="small" />
							</div>
						}
						multiple={false}
						placement="bottom-end"
						offset={size === "small" ? [0, -2] : [0, 4]}
						disabled={disabled}
						size={size}
					/>
				)}
			</div>
			{pipeText && (
				<div className="input-field__pipe-text">
					<span>{pipeText}</span>
				</div>
			)}
			<div className="DS__input-caption">
				{errorMessage && (
					<>
						<Icon name="exclamation-circle" style={{ type: "solid" }} size="small" color="#b4371b" />
						<div id={`${id}-error`} className="DS__input-error-message">
							{errorMessage}
						</div>
					</>
				)}
				{infoText && (
					<>
						<Icon name="information-circle" style={{ type: "solid" }} size="small" color="#0061c1" />
						<div id={`${id}-help`} className="DS__input-info-text">
							{infoText}
						</div>
					</>
				)}
				{captionText}
			</div>
		</div>
	);
});

export default Input;

Input.propTypes = {
	labelInfoText: PropTypes.string,
	onBlur: PropTypes.func,
	disableClear: PropTypes.bool,
	chipInputMultiple: PropTypes.bool,
	chipValues: PropTypes.arrayOf(PropTypes.object),
	onClearChips: PropTypes.func,
	setChipValues: PropTypes.func,
	allowChipModify: PropTypes.func,
	register: PropTypes.func,
	pipeText: PropTypes.string,
	keyPress: PropTypes.func,
	setValue: PropTypes.func,
	onClick: PropTypes.func,
	...InputPropTypes
};
